import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { ButtonIconProps, ButtonProps } from '@yooga-tecnologia/mantra/loader';

import { Table } from '../../domain/models/table';
import { TableService } from '../../domain/services/table.service';
import { TableApiService } from '../../services/tables/table-api.service';
import { CurrencyPipe } from '../../pipes/currency.pipe';
import { EventTrackingEnum, PageStatus, PageStatusEnum, SCREEN_VIEW_TIMEOUT_IN_MS } from '../../app.types';

import { AppBarComponent } from '../../components/app-bar/app-bar.component';
import { LoadingComponent } from '../../components/loading/loading.component';
import { StatusMessageComponent } from '../../components/status-message/status-message.component';
import { HowToPayComponent } from '../home/how-to-pay/how-to-pay.component';
import { EventService } from '../../domain/services/event.service';
import { SocketService } from '../../services/socket/socket.service';
import { TableValuesSummaryListComponent } from '../../components/table-values-summary-list/table-values-summary-list.component';
import { TableItem } from '../../domain/models/table-item';
import { UserService } from '../../domain/services/user.service';

/** os itens de uma pessoa na conta; name null = sem dono */
interface OwnerSection {
  userId: number | null;
  name: string | null;
  items: TableItem[];
}

@Component({
  selector: 'app-order-summary',
  standalone: true,
  imports: [
    CommonModule,
    LoadingComponent,
    StatusMessageComponent,
    AppBarComponent,
    CurrencyPipe,
    HowToPayComponent,
    TableValuesSummaryListComponent,
  ],
  templateUrl: './order-summary.component.html',
  styleUrl: './order-summary.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OrderSummaryPageComponent implements OnInit, OnDestroy {

  pageStatus: PageStatus = PageStatusEnum.LOADING;
  pageTitle: string = 'Conta';

  uuid: string = '';
  tableUuid!: string;
  menuUrl: string = '';
  isOpened: boolean = false;
  table: Table;
  hasPayments = false;
  cartItemsQty: number = 0;
  howToPayModal: NgxSmartModalComponent | null = null;
  backButtonProps: ButtonIconProps = {
    icon: 'arrow-left',
    color: 'neutral',
    variant: 'regular',
    size: 'medium',
  };

  closeTableButtonProps: Partial<ButtonProps> & { onClick?: () => void } = {
    label: 'Ok, combinado',
    onClick: () => this.handleBackButtonClick(),
  };

  errorButtonProps: Partial<ButtonProps> & { onClick?: () => void } = {
    label: 'Voltar ao início',
    onClick: () => this.handleBackButtonClick(),
  };

  screenViewTimeout: any;
  expandedItems: Set<number> = new Set();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private tableService: TableService,
    private tableApiService: TableApiService,
    private ngxSmartModalService: NgxSmartModalService,
    private vcr: ViewContainerRef,
    private eventService: EventService,
    private socketService: SocketService,
    private userService: UserService,
  ) {
    this.uuid = String(this.activatedRoute.snapshot.paramMap.get('uuid'));
  }

  ngOnInit(): void {
    this.tableService.$openedTable.subscribe((table: Table | null) => {
      if (table) {
        const grouped = this.tableService.tableWithGroupedItems();
        if (!grouped) return;
        this.table = grouped;
        this.isOpened = this.table.installation.isOpened;
        this.pageTitle = `Conta - ${this.table.tableName}`;
        this.hasPayments = this.table.payments.length > 0 || this.table.paid > 0;
        if (this.pageStatus !== PageStatusEnum.SUCCESS) {
          this.pageStatus = PageStatusEnum.DEFAULT;
        }

        this.screenViewTimeout = setTimeout(() => {
          this.eventService.track(EventTrackingEnum.CHECKOUT_ORDER_SUMMARY_VIEWED_PAGE);
        }, SCREEN_VIEW_TIMEOUT_IN_MS);
      } else {
        this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.screenViewTimeout) {
      clearTimeout(this.screenViewTimeout);
    }
  }

  handleBackButtonClick(): void {
    this.router.navigate(['..'], { relativeTo: this.activatedRoute });
  }

  get buttonLabel(): string {
    return this.table?.modulePaymentTable ? 'Pagar conta' : 'Fechar e chamar garçom';
  }

  /** Fatia os itens por dono: sem dono primeiro, depois quem está olhando, depois os outros. */
  get ownerSections(): OwnerSection[] {
    const loggedUserId = this.userService.getUser()?.id;
    const sections = new Map<number | null, OwnerSection>();

    for (const item of this.table.items) {
      if (item.tip) continue;

      const key = item.userId ?? null;
      if (!sections.has(key)) {
        sections.set(key, { userId: key, name: key == null ? null : item.userName, items: [] });
      }
      sections.get(key)!.items.push(item);
    }

    return [...sections.values()].sort((a, b) => {
      if (a.userId == null) return -1;
      if (b.userId == null) return 1;
      if (a.userId === loggedUserId) return -1;
      if (b.userId === loggedUserId) return 1;
      return 0;
    });
  }

  get serviceFeeValue(): number {
    const serviceFeeItem = this.table.items.find((item: any) => item.tip);
    return serviceFeeItem ? serviceFeeItem.valueGrouped || serviceFeeItem.value : 0;
  }

  hasComplementos(item: any): boolean {
    return (item.options && item.options.length > 0) || !!item.additionalInfo;
  }

  hasMoreThanOneComplemento(item: any): boolean {
    const optionsCount = item.options ? item.options.length : 0;
    const hasAdditionalInfo = !!item.additionalInfo;
    return (optionsCount + (hasAdditionalInfo ? 1 : 0)) > 1;
  }

  getFirstComplemento(item: any): any {
    if (item.options && item.options.length > 0) {
      return { type: 'option', data: item.options[0] };
    }
    if (item.additionalInfo) {
      return { type: 'additionalInfo', data: item.additionalInfo };
    }
    return null;
  }

  isExpanded(itemId: number): boolean {
    return this.expandedItems.has(itemId);
  }

  toggleExpand(itemId: number): void {
    if (this.expandedItems.has(itemId)) {
      this.expandedItems.delete(itemId);
    } else {
      this.expandedItems.add(itemId);
    }
  }

  private goToDetails() {
    this.router.navigate(['../details'], { relativeTo: this.activatedRoute });
  }

  handleCloseTable(): void {
    if (this.table.modulePaymentTable) {
      const howToPayModal = this.ngxSmartModalService.create(
        'how-to-pay',
        HowToPayComponent,
        this.vcr,
        {
          customClass: 'dialog-small',
        }
      ).open();

      this.eventService.track(EventTrackingEnum.CHECKOUT_ORDER_SUMMARY_CLICKED_ADD_PAYMENT);

      howToPayModal.onClose.subscribe((modal: NgxSmartModalComponent) => {
        if (modal.hasData()) {
          this.goToDetails();
        } else {
          this.howToPayModal?.close();
        }
      });
      return;
    }

    this.pageStatus = PageStatusEnum.LOADING;

    this.tableApiService.callWaiter(this.table.tableId).subscribe({
      next: (response) => {
        this.pageStatus = PageStatusEnum.SUCCESS;
        this.socketService.emit(
          'bot:update',
          JSON.stringify({
            table: String(this.table.tableId),
          }),
        );
        this.socketService.emit(
          'mesa:update',
          JSON.stringify({
            uuid: crypto.randomUUID(),
          }),
        );
        this.eventService.track(EventTrackingEnum.CHECKOUT_ORDER_SUMMARY_CLICKED_CALL_WAITER);
      },
      error: (error) => {
        console.error('[TABLE-DETAILS] Error calling waiter:', error);
        this.pageStatus = PageStatusEnum.ERROR;
      }
    });
  }
}
